import { ReactElement, useCallback, useRef, useState } from "react";
import _ from "lodash";
import { ReactComponent as WarningIcon } from "../../assets/img/warning-icon.svg";
import {
  Button,
  Modal,
  UncontrolledTooltip,
  ModalHeader,
  ModalFooter,
  ModalBody,
} from "reactstrap";
import { useStores } from "store/useStore";
import { observer } from "mobx-react";
import { emailsSentMetricName } from "encharge-domain/lib/helpers/emailSentMetricsHelper";
import { canSpamIssueNameTemplate } from "encharge-domain/lib/helpers/can_spam_issue_name";

/**
 * The emails whose sends are held for CAN-SPAM, as links. Mounted only while
 * the dialog is open: reading a name loads the account's whole email list.
 */
const HeldEmails = observer(({ ids }: { ids: IEmailContent["id"][] }) => {
  const { emailsStore } = useStores();
  return (
    <div>
      <h6 className="font-weight-bold">Some emails are on hold</h6>
      <p>
        These emails have no working unsubscribe link or no mailing address, so
        they are not sent. Sends are retried for 7 days, then dropped. Open each
        email, fix it and save it.
      </p>
      <ul className="mb-0">
        {ids.map((id) => (
          <li key={id}>
            <a href={`/emails?email=${id}&emails-folder-item=allEmails`}>
              {emailsStore.getEmailById(id)?.name || `Email ${id}`}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
});

const AccountWarnings = () => {
  const iconRef = useRef<any>();
  const { accountStore } = useStores();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const toggleDialog = useCallback(() => {
    setIsDialogOpen(!isDialogOpen);
  }, [isDialogOpen, setIsDialogOpen]);

  const account = accountStore.account;
  if (!account) return null;

  // Retrieve issues for this account
  const issues = accountStore.accountIssues.current();
  if (accountStore.accountIssues.isLoading()) {
    return null;
  }
  const hasIssues = issues?.length > 0;
  if (!hasIssues) {
    return null;
  }
  let warning: ReactElement | null = null;
  let title: string = "";

  const errors = _.filter(issues, (issue) => issue.priority === "error");
  // Emails held for CAN-SPAM: each held send records a 'warning' issue named
  // after the email (checkCanSpamCompliance). Never 'error', which would hold
  // every send of the account (V2-H4)
  const heldEmailIds = _.uniq(
    _.compact(
      _.map(issues, (issue) =>
        issue.name.startsWith(canSpamIssueNameTemplate)
          ? Number(issue.name.slice(canSpamIssueNameTemplate.length))
          : undefined
      )
    )
  );
  const isSendingPausedForVolume = _.some(
    issues,
    (issue) =>
      issue.priority === "error" && issue.name.startsWith(emailsSentMetricName)
  );

  if (
    isSendingPausedForVolume &&
    // Make sure sending volume is the only error
    errors.length === 1
  ) {
    title = "Email Sending Warm Up";
    warning = (
      <div>
        Thank you for sending your first email with Encharge.
        <br />
        <br />
        Since this is your first email, we have to manually review the content
        of your email before it goes out. Thank you for your patience.
        <br />
        <br />
        If you need immediate assistance, you can{" "}
        <a href="mailto:support@encharge.io">write to us</a>.
      </div>
    );
  } else if (errors.length > 0) {
    title = "Email Sending Paused";
    warning = (
      <div>
        <p>
          Due to unusual activity on your account, we have temporarily disabled
          email sending.
        </p>
        <p>Our team will review it manually. </p>
        <p>
          Please note that your emails are currently paused but should go out
          shortly.
        </p>
        <p>
          If you need immediate assistance, you can{" "}
          <a href="mailto:support@encharge.io">write to us</a>.
        </p>
      </div>
    );
  }

  if (!warning && !heldEmailIds.length) {
    return null;
  }
  if (!warning) {
    title = "Emails On Hold";
  }
  return (
    <>
      <div className="account-warnings-container mr-3">
        <span className="cursor-pointer warning-active" onClick={toggleDialog}>
          <WarningIcon ref={iconRef} />
        </span>
        {iconRef.current && (
          <UncontrolledTooltip target={iconRef.current}>
            There are issues with your account. <br />
            Click for more information.
          </UncontrolledTooltip>
        )}
        <Modal
          isOpen={isDialogOpen}
          toggle={toggleDialog}
          zIndex={1400}
          size={"lg"}
        >
          <ModalHeader>{title}</ModalHeader>
          <ModalBody>
            {hasIssues && warning}
            {heldEmailIds.length > 0 && (
              <div className={warning ? "mt-4" : undefined}>
                <HeldEmails ids={heldEmailIds} />
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={toggleDialog}>
              Close
            </Button>{" "}
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
};
export default observer(AccountWarnings);
